import createApolloClient from "./graphql/ApolloClient";
import {
  GET_EXISTING_ORDER_CART,
  GET_WISHLIST_DATA,
  INSERT_CART,
  MODIFY_CART_ITEMS,
  REMOVE_CART_ITEM,
} from "@/graphql/queries";
import { CART_STATUS_ENUM } from "./app.enum.js";
import Cookies from "js-cookie";
import { setCartId } from "./redux/cartSlice";
import {
  ADD_VARIANT_SIZES,
  UPDATE_CART_ITEM_SIZE_QUANTITY,
  DELETE_CART_ITEM_SIZE_SOFT,
} from "./graphql/mutations";
import { verifyRealtimeStock } from "./stockVerification";

const client = createApolloClient();

export const fetchWishlistData = async () => {
  try {
    const { data: wishlistData } = await client.query({
      query: GET_WISHLIST_DATA,
    });
    return wishlistData?.order_wishlist || [];
  } catch (error) {
    console.log(error);
  }
};

export const modifyItemsInCartAPI = async (product, dispatch, freezeStock) => {
  let cart_id = "";
  let items = [];
  const userId = Cookies.get("id");

  console.log("🧾 modifyItemsInCartAPI called");
  console.log("product:", product);
  console.log("wholesaleSelections:", product?.wholesaleSelections);

  try {
    const { data } = await client.query({
      query: GET_EXISTING_ORDER_CART,
      variables: {
        where: {
          processed: { _eq: false },
          status: { _eq: CART_STATUS_ENUM.NEW },
          user_id: { _eq: userId },
        },
      },
      fetchPolicy: "network-only",
    });

    const activeCart = data.order_carts.find(
      (cart) => cart.cart_items && cart.cart_items.length > 0
    );

    if (activeCart) {
      cart_id = activeCart.id;
      items = activeCart.cart_items;
    } else if (data.order_carts.length > 0) {
      cart_id = data.order_carts[0].id;
      items = data.order_carts[0].cart_items || [];
    } else {
      const generateOrderNumber = () => {
        const now = new Date();
        const yy = String(now.getFullYear()).slice(-2);
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const chars = "ABCDEFGHIJKLMNPQRSTUVWXYZ123456789";
        let random = "";
        for (let i = 0; i < 4; i++) {
          random += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `SG-${yy}${mm}${random}`;
      };

      const orderNumber = generateOrderNumber();
      const { data: insertData } = await client.mutate({
        mutation: INSERT_CART,
        variables: {
          object: {
            status: CART_STATUS_ENUM.NEW,
            user_id: userId,
            order_number: orderNumber,
          },
        },
      });
      cart_id = insertData.insert_order_carts_one.id;
    }

    dispatch(setCartId(cart_id));

    const hasWholesaleSelections =
      Array.isArray(product.wholesaleSelections) && product.wholesaleSelections.length > 0;

    console.log(
      `📦 Product type: ${
        hasWholesaleSelections
          ? "Multiple sizes (wholesale)"
          : product.variant_size_id
          ? "Single size"
          : "No sizes"
      }`
    );

    // ==================== VALIDATION ====================
    const isWholesale = (product.sub_category_name ?? '').toLowerCase().includes('wholesale');
    if (isWholesale) {
      console.log('⚡ Wholesale product — skipping all stock validation');
    } else if (hasWholesaleSelections) {
      console.log(`📦 Validating ${product.wholesaleSelections.length} wholesale selections`);

      for (const selection of product.wholesaleSelections.filter((s) => s.qty > 0)) {
        let totalExistingQty = 0;

        items.forEach((item) => {
          if (
            item.product_id === product.product_id &&
            (item.product_variation_id ?? null) === (product.product_variation_id ?? null)
          ) {
            (item.cart_item_sizes || []).forEach((size) => {
              if (size.is_deleted) return;
              if (
                size.variant_size_id === selection.variantSizeId &&
                size.metal === (selection.metal || "")
              ) {
                totalExistingQty += size.quantity || 0;
              }
            });
          }
        });

        const newTotal = totalExistingQty + selection.qty;
        const originalQty = selection.original_stock || selection.maxQty;

        console.log(
          `   Size ${selection.variantSizeId}: Original ${originalQty}, Existing (all platings) ${totalExistingQty}, Adding ${selection.qty}, New Total ${newTotal}`
        );

        if (originalQty !== undefined && newTotal > originalQty) {
          const available = originalQty - totalExistingQty;
          throw new Error(
            available <= 0
              ? `Size ${selection.size_value || selection.variantSizeId} out of stock (${totalExistingQty} in cart across all plating options)`
              : `Size ${selection.size_value || selection.variantSizeId}: Only ${available} more available (you have ${totalExistingQty} in cart across all plating options)`
          );
        }
      }
} else if (!isWholesale && product.variant_size_id && product.original_stock !== undefined) {
      console.log(`📦 Validating single sized product: ${product.variant_size_id}`);

      if (product.quantity <= 0) throw new Error(`Quantity must be greater than 0`);

      let totalExistingQtyForSize = 0;

      items.forEach((item) => {
        if (
          item.product_id === product.product_id &&
          (item.product_variation_id ?? null) === (product.product_variation_id ?? null)
        ) {
          (item.cart_item_sizes || []).forEach((size) => {
            if (size.is_deleted) return;
            if (
              size.variant_size_id === product.variant_size_id &&
              size.metal === (product.metal || "")
            ) {
              totalExistingQtyForSize += size.quantity || 0;
            }
          });
        }
      });

      const newTotal = totalExistingQtyForSize + product.quantity;
      const originalQty = product.original_stock;

      console.log(`🔍 STOCK CHECK:
  - Original stock (from DB): ${originalQty}
  - Total existing in cart (all plating variants): ${totalExistingQtyForSize}
  - Available: ${originalQty - totalExistingQtyForSize}
  - Adding now: ${product.quantity}
  - New total: ${newTotal}`);

      if (newTotal > originalQty) {
        const available = originalQty - totalExistingQtyForSize;
        throw new Error(
          available <= 0
            ? `Out of stock (${totalExistingQtyForSize} already in cart across all plating options)`
            : `Only ${available} more available (you have ${totalExistingQtyForSize} in cart across all plating options)`
        );
      }
} else if (!isWholesale && !product.variant_size_id && !hasWholesaleSelections) {
      console.log(`📦 Validating simple product (no sizes)`);

      if (product.quantity <= 0) throw new Error(`Quantity must be greater than 0`);

      const stockToCheck = product.original_stock ?? product.maxQty;

      if (stockToCheck !== undefined && stockToCheck !== null) {
        let existingQty = 0;

        items.forEach((item) => {
          if (
            item.product_id === product.product_id &&
            (item.product_variation_id ?? null) === (product.product_variation_id ?? null) &&
            !item.is_deleted
          ) {
            existingQty += item.quantity || 0;
          }
        });

        const newTotal = existingQty + product.quantity;

        console.log(`🔍 STOCK CHECK:
  - Original stock (from DB): ${stockToCheck}
  - Existing in cart: ${existingQty}
  - Available: ${stockToCheck - existingQty}
  - Adding now: ${product.quantity}
  - New total: ${newTotal}`);

        if (newTotal > stockToCheck) {
          const available = stockToCheck - existingQty;
          throw new Error(
            available <= 0
              ? `Out of stock (${existingQty} already in cart)`
              : `Only ${available} more available (you have ${existingQty} in cart)`
          );
        }
      }
    }

    console.log("✅ VALIDATION PASSED");

    await verifyRealtimeStock(product, items);

    const existingItem = items.find((item) => {
      const sameProduct =
        item.product_id === product.product_id &&
        (item.product_variation_id ?? null) === (product.product_variation_id ?? null) &&
        !item.is_deleted;

      if (hasWholesaleSelections) return sameProduct;

      if (product.variant_size_id) {
        const sizes = item.cart_item_sizes || [];
        return (
          sameProduct &&
          sizes.some(
            (size) =>
              size.variant_size_id === product.variant_size_id &&
              size.metal === (product.metal || "") &&
              (size.plating_id ?? null) === (product.plating_id ?? null) &&
              !size.is_deleted
          )
        );
      }

      return sameProduct;
    });

    let cart_item_id;

    if (existingItem) {
      cart_item_id = existingItem.id;
      console.log("🧩 Existing cart item found:", cart_item_id);

      if (hasWholesaleSelections) {
        console.log(`📦 Processing ${product.wholesaleSelections.length} wholesale sizes`);

        for (const selection of product.wholesaleSelections.filter((s) => s.qty > 0)) {
          const matchedSize = (existingItem.cart_item_sizes || []).find(
            (size) =>
              size.variant_size_id === selection.variantSizeId &&
              size.metal === (selection.metal || "") &&
              (size.plating_id ?? null) === (selection.plating_id ?? null) &&
              !size.is_deleted
          );

          if (matchedSize?.id) {
            const newQuantity = (matchedSize.quantity || 0) + selection.qty;
            await client.mutate({
              mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
              variables: {
                id: matchedSize.id,
                quantity: newQuantity,
                metal: selection.metal || "",
                plating_id: selection.plating_id || null,
              },
            });
            console.log(`✅ Updated size ${selection.variantSizeId} from ${matchedSize.quantity} to ${newQuantity}`);
          } else {
            await client.mutate({
              mutation: ADD_VARIANT_SIZES,
              variables: {
                objects: [{
                  cart_id,
                  cart_item_id,
                  variant_size_id: selection.variantSizeId,
                  metal: selection.metal || "",
                  quantity: selection.qty,
                  plating_id: selection.plating_id || null,
                  is_deleted: false,
                }],
              },
            });
            console.log(`✅ Added new size ${selection.variantSizeId}`);
          }
        }

        const totalQty = product.wholesaleSelections
          .filter((s) => s.qty > 0)
          .reduce((sum, s) => sum + s.qty, 0);

        await client.mutate({
          mutation: MODIFY_CART_ITEMS,
          variables: {
            objects: [{
              cart_id,
              product_id: product.product_id,
              product_variation_id: product.product_variation_id || null,
               price_per_gm: product.price_per_gm ?? null,
              quantity: (existingItem.quantity || 0) + totalQty,
              deleted_at: null,
              is_deleted: false,
            }],
          },
        });
        console.log(`✅ Updated cart item total quantity`);

      } else if (product.variant_size_id) {
        const matchedSize = (existingItem.cart_item_sizes || []).find(
          (size) =>
            size.variant_size_id === product.variant_size_id &&
            size.metal === (product.metal || "") &&
            (size.plating_id ?? null) === (product.plating_id ?? null) &&
            !size.is_deleted
        );

        if (matchedSize?.id) {
          const newQuantity = (matchedSize.quantity || 0) + product.quantity;
          await client.mutate({
            mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
            variables: {
              id: matchedSize.id,
              quantity: newQuantity,
              metal: product.metal || "",
              plating_id: product.plating_id || null,
            },
          });
          console.log(`✅ Updated size from ${matchedSize.quantity} to ${newQuantity}`);
        } else {
          await client.mutate({
            mutation: ADD_VARIANT_SIZES,
            variables: {
              objects: [{
                cart_id,
                cart_item_id,
                variant_size_id: product.variant_size_id,
                metal: product.metal || "",
                quantity: product.quantity,
                plating_id: product.plating_id || null,
                is_deleted: false,
              }],
            },
          });
          console.log("✅ Added new size");
        }

        await client.mutate({
          mutation: MODIFY_CART_ITEMS,
          variables: {
            objects: [{
              cart_id,
              product_id: product.product_id,
              product_variation_id: product.product_variation_id || null,
              quantity: (existingItem.quantity || 0) + product.quantity,
              price_per_gm: product.price_per_gm ?? null,
              is_deleted: false,
              deleted_at: null,
            }],
          },
        });

      } else {
        const newQuantity = (existingItem.quantity || 0) + product.quantity;
        await client.mutate({
          mutation: MODIFY_CART_ITEMS,
          variables: {
            objects: [{
              cart_id,
              product_id: product.product_id,
              product_variation_id: product.product_variation_id || null,
              price_per_gm: product.price_per_gm ?? null, 
              quantity: newQuantity,
              is_deleted: false,
              deleted_at: null,
            }],
          },
        });
        console.log(`✅ Updated cart item from ${existingItem.quantity} to ${newQuantity}`);
      }
    } else {
      console.log("Creating new cart item");

      const { data: cartItemResult } = await client.mutate({
        mutation: MODIFY_CART_ITEMS,
        variables: {
          objects: [{
            product_id: product.product_id,
            product_variation_id: product.product_variation_id || null,
            cart_id,
            type: CART_STATUS_ENUM.NEW,
             price_per_gm: product.price_per_gm ?? null,
            quantity: product.quantity,
            deleted_at: null,
            is_deleted: false,
          }],
        },
      });

      cart_item_id = cartItemResult?.insert_order_cart_items?.returning?.[0]?.id;
      console.log("🆕 Created cart_item:", cart_item_id);
      if (!cart_item_id) {
        console.error("❌ MODIFY_CART_ITEMS did not return a cart_item_id — aborting to avoid orphaned item", {
          product_id: product.product_id,
          product_variation_id: product.product_variation_id,
          cart_id,
          rawResult: cartItemResult,
        });
        throw new Error("Failed to create cart item — please try adding this product again");
      }
      if (hasWholesaleSelections) {
        const sizeObjects = product.wholesaleSelections
          .filter((s) => s.qty > 0)
          .map((selection) => ({
            cart_id,
            cart_item_id,
            variant_size_id: selection.variantSizeId,
            metal: selection.metal || "",
            quantity: selection.qty,
            plating_id: selection.plating_id || null,
            is_deleted: false,
          }));

        if (sizeObjects.length > 0) {
          await client.mutate({
            mutation: ADD_VARIANT_SIZES,
            variables: { objects: sizeObjects },
          });
          console.log(`✅ Added ${sizeObjects.length} wholesale sizes`);
        }
      } else if (product.variant_size_id) {
        await client.mutate({
          mutation: ADD_VARIANT_SIZES,
          variables: {
            objects: [{
              cart_id,
              cart_item_id,
              variant_size_id: product.variant_size_id,
              metal: product.metal || "",
              quantity: product.quantity,
              plating_id: product.plating_id || null,
              is_deleted: false,
            }],
          },
        });
        console.log("✅ Added size data");
      }
    }

    return { cart_id, cart_item_id };

  } catch (error) {
    console.error("❌ Error:", error);
    throw error;
  }
};

export const updateCartItemQuantity = async (cartItemSize, newQuantity, dispatch) => {
  try {
    if (newQuantity < 0) throw new Error("Quantity cannot be negative");

    const userId = Cookies.get("id");
    const { data } = await client.query({
      query: GET_EXISTING_ORDER_CART,
      variables: {
        where: {
          processed: { _eq: false },
          status: { _eq: CART_STATUS_ENUM.NEW },
          user_id: { _eq: userId },
        },
      },
      fetchPolicy: "network-only",
    });

    const activeCart = data.order_carts.find(
      (cart) => cart.cart_items && cart.cart_items.length > 0
    );

    if (!activeCart) throw new Error("Cart not found");

    await client.mutate({
      mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
      variables: {
        id: cartItemSize.id,
        quantity: newQuantity,
        metal: cartItemSize.metal || "",
        plating_id: cartItemSize.plating_id || null,
      },
    });

    return { success: true, cart_id: activeCart.id };
  } catch (error) {
    console.error("❌ Error:", error);
    throw error;
  }
};

export const reduceCartItemQuantity = async (product, quantityToReduce = 1) => {
  try {
    const userId = Cookies.get("id");
    const { data } = await client.query({
      query: GET_EXISTING_ORDER_CART,
      variables: {
        where: {
          processed: { _eq: false },
          status: { _eq: CART_STATUS_ENUM.NEW },
          user_id: { _eq: userId },           // ✅ always had this — fine
        },
      },
      fetchPolicy: "network-only",
    });

    const activeCart = data.order_carts.find(
      (cart) => cart.cart_items && cart.cart_items.length > 0
    );

    if (!activeCart) throw new Error("Cart not found");

    const cartId = activeCart.id;
    const cartItem = activeCart.cart_items.find(
      (item) =>
        item.product_id === product.product_id &&
        (item.product_variation_id ?? null) === (product.product_variation_id ?? null)
    );

    if (!cartItem) throw new Error("Item not found in cart");

    if (product.variant_size_id) {
      const sizeToReduce = cartItem.cart_item_sizes?.find(
        (size) =>
          size.variant_size_id === product.variant_size_id &&
          size.metal === (product.metal || "") &&
          (size.plating_id ?? null) === (product.plating_id ?? null) &&
          !size.is_deleted
      );

      if (!sizeToReduce) throw new Error("Size not found in cart");

      const currentQty = sizeToReduce.quantity || 0;
      const newQty = Math.max(0, currentQty - quantityToReduce);

      console.log(`🔻 Reducing size ${product.variant_size_id} from ${currentQty} to ${newQty}`);

      if (newQty === 0) {
        await client.mutate({
          mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
          variables: {
            id: sizeToReduce.id,
            quantity: 0,
            metal: product.metal || "",
            plating_id: product.plating_id || null,
          },
        });
        await client.mutate({
          mutation: DELETE_CART_ITEM_SIZE_SOFT,
          variables: { id: sizeToReduce.id },
        });

        const remainingActive = cartItem.cart_item_sizes.filter(
          (s) => s.id !== sizeToReduce.id && !s.is_deleted
        );

        if (remainingActive.length === 0) {
          await client.mutate({
            mutation: REMOVE_CART_ITEM,
            variables: {
              _set: { deleted_at: new Date(), is_deleted: true },
              where: { id: { _eq: cartItem.id } },
            },
          });
        }
      } else {
        await client.mutate({
          mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
          variables: {
            id: sizeToReduce.id,
            quantity: newQty,
            metal: product.metal || "",
            plating_id: product.plating_id || null,
          },
        });
      }
    } else {
      const currentQty = cartItem.quantity || 0;
      const newQty = Math.max(0, currentQty - quantityToReduce);

      console.log(`🔻 Reducing item from ${currentQty} to ${newQty}`);

      if (newQty === 0) {
        await client.mutate({
          mutation: REMOVE_CART_ITEM,
          variables: {
            _set: { quantity: 0 },
            where: { id: { _eq: cartItem.id } },
          },
        });
        await client.mutate({
          mutation: REMOVE_CART_ITEM,
          variables: {
            _set: { deleted_at: new Date(), is_deleted: true },
            where: { id: { _eq: cartItem.id } },
          },
        });
      } else {
        await client.mutate({
          mutation: MODIFY_CART_ITEMS,
          variables: {
            objects: [{
              cart_id: cartId,
              product_id: product.product_id,
              product_variation_id: product.product_variation_id || null,
              quantity: newQty,
              deleted_at: null,
              is_deleted: false,
            }],
          },
        });
      }
    }

    return { success: true, cart_id: cartId };
  } catch (error) {
    console.error("❌ Error reducing quantity:", error);
    throw error;
  }
};

// ✅ FIX: Added user_id filter — previously missing, causing activeCart to
// always be null and silently returning without firing any mutations.
export const removeItemFromCart = async (product) => {
  const userId = Cookies.get("id");                              // ← NEW

  const { data } = await client.query({
    query: GET_EXISTING_ORDER_CART,
    variables: {
      where: {
        processed: { _eq: false },
        status:    { _eq: CART_STATUS_ENUM.NEW },
        user_id:   { _eq: userId },                              // ← NEW
      },
    },
    fetchPolicy: "network-only",
  });

  const activeCart = data.order_carts.find(
    (cart) => cart.cart_items && cart.cart_items.length > 0
  );

  if (!activeCart) {
    console.warn("removeItemFromCart: no active cart found for user", userId);
    return;
  }

  const cartItem = activeCart.cart_items.find(
    (item) =>
      item.product_id === product.product_id &&
      (item.product_variation_id ?? null) === (product.product_variation_id ?? null)
  );

  if (!cartItem) {
    console.warn("removeItemFromCart: item not found in cart", product.product_id);
    return;
  }

  if (product.variant_size_id) {
    const sizeToRemove = cartItem.cart_item_sizes?.find(
      (size) =>
        size.variant_size_id === product.variant_size_id &&
        size.metal === (product.metal || "") &&
        (size.plating_id ?? null) === (product.plating_id ?? null) &&
        !size.is_deleted
    );

    if (sizeToRemove) {
      await client.mutate({
        mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
        variables: {
          id: sizeToRemove.id,
          quantity: 0,
          metal: product.metal || "",
          plating_id: product.plating_id || null,
        },
      });
      await client.mutate({
        mutation: DELETE_CART_ITEM_SIZE_SOFT,
        variables: { id: sizeToRemove.id },
      });

      const remainingActive = cartItem.cart_item_sizes.filter(
        (s) => s.id !== sizeToRemove.id && !s.is_deleted
      );

      if (remainingActive.length === 0) {
        await client.mutate({
          mutation: REMOVE_CART_ITEM,
          variables: {
            _set: { deleted_at: new Date(), is_deleted: true },
            where: { id: { _eq: cartItem.id } },
          },
        });
      }
    }
  } else {
    await client.mutate({
      mutation: REMOVE_CART_ITEM,
      variables: {
        _set: { quantity: 0 },
        where: { id: { _eq: cartItem.id } },
      },
    });
    await client.mutate({
      mutation: REMOVE_CART_ITEM,
      variables: {
        _set: { deleted_at: new Date(), is_deleted: true },
        where: { id: { _eq: cartItem.id } },
      },
    });
  }
};

export const clearCartAPI = async () => {
  try {
    const userId = Cookies.get("id");

    console.log("clear cart hit");

    const { data } = await client.query({
      query: GET_EXISTING_ORDER_CART,
      variables: {
        where: {
          processed: { _eq: false },
          status: { _eq: CART_STATUS_ENUM.NEW },
          user_id: { _eq: userId },
        },
      },
      fetchPolicy: "network-only",
    });

    const activeCart = data.order_carts.find(
      (cart) => cart.cart_items && cart.cart_items.length > 0
    );

    if (!activeCart) {
      console.log("No active cart found");
      return { success: true, message: "No cart to clear" };
    }

    const cartId = activeCart.id;
    const cartItems = activeCart.cart_items;

    console.log(`🗑️ Clearing cart ${cartId} with ${cartItems.length} items`);

    for (const cartItem of cartItems) {
      if (cartItem.is_deleted) continue;

      if (cartItem.cart_item_sizes && cartItem.cart_item_sizes.length > 0) {
        for (const size of cartItem.cart_item_sizes) {
          if (size.is_deleted) continue;
          try {
            await client.mutate({
              mutation: UPDATE_CART_ITEM_SIZE_QUANTITY,
              variables: { id: size.id, quantity: 0, metal: size.metal || "", plating_id: size.plating_id || null },
            });
            await client.mutate({
              mutation: DELETE_CART_ITEM_SIZE_SOFT,
              variables: { id: size.id },
            });
            console.log(`  ✓ Deleted size ${size.id}`);
          } catch (error) {
            console.error(`  ✗ Failed to delete size ${size.id}:`, error);
          }
        }
      }

      try {
        await client.mutate({
          mutation: REMOVE_CART_ITEM,
          variables: { _set: { quantity: 0 }, where: { id: { _eq: cartItem.id } } },
        });
        await client.mutate({
          mutation: REMOVE_CART_ITEM,
          variables: { _set: { deleted_at: new Date(), is_deleted: true }, where: { id: { _eq: cartItem.id } } },
        });
        console.log(`  ✓ Deleted cart item ${cartItem.id}`);
      } catch (error) {
        console.error(`  ✗ Failed to delete cart item ${cartItem.id}:`, error);
      }
    }

    console.log("✅ Cart cleared successfully");
    return { success: true, cart_id: cartId };

  } catch (error) {
    console.error("❌ Error clearing cart:", error);
    throw error;
  }
};